import {ReactNode} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {SxProps, Theme} from '@mui/material/styles';
import PrimaryButton from '../StyledComponents/PrimaryButton';
import {fonts, kit, radius} from './tokens';

interface EmptyStateProps {
  // Icon node (e.g. a MUI icon), shown in a tinted rounded tile.
  icon?: ReactNode;
  headline: string;
  subline?: string;
  // Primary CTA -- both must be set to render the button.
  ctaLabel?: string;
  onCtaClick?: () => void;
  // Optional secondary text-button under the CTA.
  secondaryLabel?: string;
  onSecondaryClick?: () => void;
  sx?: SxProps<Theme>;
}

// Centered empty state: tinted icon tile + serif headline + slate subline +
// one primary CTA (web port of the mobile kit EmptyState). Never render a
// blank box -- use this.
const EmptyState = ({
  icon,
  headline,
  subline,
  ctaLabel,
  onCtaClick,
  secondaryLabel,
  onSecondaryClick,
  sx,
}: EmptyStateProps) => (
  <Box
    sx={[
      {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '48px 32px',
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}>
    {icon ? (
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: `${radius.xl}px`,
          backgroundColor: kit.blueTint,
          color: kit.blueInk,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '18px',
        }}>
        {icon}
      </Box>
    ) : null}
    <Typography
      variant="h5"
      sx={{
        fontFamily: fonts.serif,
        fontSize: 22,
        lineHeight: 1.25,
        color: kit.ink,
      }}>
      {headline}
    </Typography>
    {subline ? (
      <Typography
        sx={{
          marginTop: '8px',
          maxWidth: 420,
          fontSize: 14,
          lineHeight: 1.45,
          color: kit.slate,
        }}>
        {subline}
      </Typography>
    ) : null}
    {ctaLabel && onCtaClick ? (
      <Box sx={{marginTop: '22px'}}>
        <PrimaryButton onClick={onCtaClick}>{ctaLabel}</PrimaryButton>
      </Box>
    ) : null}
    {secondaryLabel && onSecondaryClick ? (
      <Button
        variant="text"
        onClick={onSecondaryClick}
        sx={{
          marginTop: '10px',
          textTransform: 'none',
          fontSize: 14,
          color: kit.blueInk,
        }}>
        {secondaryLabel}
      </Button>
    ) : null}
  </Box>
);

export default EmptyState;
